import { useRef } from "react";

import {
  hashKey,
  InfiniteData,
  Query,
  QueryKey,
  UseQueryOptions,
  useInfiniteQuery,
  useQuery,
} from "@tanstack/react-query";
import { AxiosError, isAxiosError } from "axios";
import { omit } from "lodash-es";
import { validate as isValidUUID } from "uuid";

import { historyApi } from "src/api/endpoints";
import {
  decisionHistoryApiRoute,
  decisionHistoryV3ApiRoute,
} from "src/api/exporterApi";
import { GenericObject } from "src/api/flowTypes";
import {
  DecideDecisionError,
  DecideDecisionSuccess,
  DecisionEnvironment,
  DecisionHistoryError,
} from "src/api/types";
import { DateRange } from "src/base-components/DateRangePicker";
import {
  DecisionList,
  DecisionsApiGetDecisionHistoryApiV3DecisionsDecisionIdGetRequest,
  DecisionsApiListDecisionsHistoryApiV3DecisionsGetRequest,
  HistoricalDecisionApiResponse,
} from "src/clients/history_v3";
import { EQUALITY_OPERATORS } from "src/constants/operators";
import { JSONValue } from "src/datasets/DatasetTable/types";
import { DecisionStatusCode } from "src/flow/decisionHistory/SharedStatusColumn";
import {
  useIsIcebergEnabled,
  useIsIcebergEnabledSafe,
} from "src/hooks/useIsIcebergEnabled";
import { logger } from "src/utils/logger";

// Decision history list endpoints share a 5.5 MiB serialize_payload cap on the
// exporter Lambda; oversized pages surface to the UI as HTTP 413 (OPTI-6893).
// On 413 we halve the page size and retry from the same cursor — fewer rows
// per page, no data lost. Hooks cache the discovered safe size on a ref,
// reset when the queryKey changes so it doesn't leak into unrelated queries.
export const DECISION_HISTORY_DEFAULT_PAGE_SIZE = 100;
export const DECISION_HISTORY_MIN_PAGE_SIZE = 1;

export const fetchDecisionsWithPageSizeFallback = async <T,>(
  fetcher: (pageSize: number) => Promise<T>,
  pageSize: number,
): Promise<{ data: T; pageSize: number }> => {
  try {
    const data = await fetcher(pageSize);
    return { data, pageSize };
  } catch (error) {
    const nextPageSize = Math.max(
      DECISION_HISTORY_MIN_PAGE_SIZE,
      Math.floor(pageSize / 2),
    );
    const canRetry =
      isAxiosError(error) &&
      error.response?.status === 413 &&
      nextPageSize < pageSize;
    if (!canRetry) {
      throw error;
    }
    return fetchDecisionsWithPageSizeFallback(fetcher, nextPageSize);
  }
};

export type DecisionHistorySuccessResponse<
  ResponseData extends GenericObject = GenericObject,
> = Pick<DecideDecisionSuccess<ResponseData>, "data" | "metadata" | "status">;

export type DecisionHistoryErrorResponse = Pick<
  DecideDecisionError,
  "detail" | "metadata" | "status"
>;

export type EqualityOperator =
  (typeof EQUALITY_OPERATORS)[keyof typeof EQUALITY_OPERATORS]["code"];

export type SingleOutcomeFilter = {
  outcome_type_key: string;
  filter: {
    property: string;
    operator: EqualityOperator;
    value: string | number | boolean;
  };
};

export type MultipleOutcomeFilter = {
  outcome_type_key: string;
  filter: {
    property: string;
    operator: "in" | "not_in";
    values: (string | number | boolean)[];
  };
};

export type OutcomePresenceFilter = {
  outcome_type_key: string;
  filter: "exists" | "not_exists";
};

export type DecisionsOutcomeFilter =
  | OutcomePresenceFilter
  | SingleOutcomeFilter
  | MultipleOutcomeFilter;

export type DecisionHistoryIntegrationDuration = {
  type: string;
  node_id: string | null;
  request_id: string;
  provider: string;
  resource: string;
  provider_response_time: number;
  provider_response_status: "error" | "success" | "cache" | "timeout";
  request_attempts: number;
};

export type DecisionHistoryRecordV2<
  ResponseData extends GenericObject = GenericObject,
> =
  // To be able to discriminate the response type by the is_error field
  (
    | {
        is_error: false;
        response: DecisionHistorySuccessResponse<ResponseData>;
      }
    | { is_error: true; response: DecisionHistoryErrorResponse }
  ) & {
    id: string;
    flow: {
      id: string;
      slug: string;
      version_id: string;
      version_name: string;
    };
    entity_id: string;
    is_error: boolean;
    error: DecisionHistoryError | null;
    is_sandbox: boolean;
    duration_in_milliseconds: number;
    start_time: string; // ISO
    end_time: string; // ISO
    masked_fields: unknown;
    request: unknown;
    /**
     * Only includes data if the endpoint is called with the option to include node results
     */
    node_results: {
      nodes: {
        [nodeId: string]: {
          id: string;
          title: string;
          tracing: {
            start_time?: string;
            end_time?: string;
            error?: unknown;
          };
          data: GenericObject;
        };
      };
      node_execution_metadata?: {
        [nodeId: string]: {
          variables?: {
            [key: string]: {
              value: JSONValue;
              type_: string | null | undefined;
            };
          };
          expected_output_matched?: boolean;
          after_execution_stripped_data_object_size_bytes?: number;
        };
      };
    };
    external_resources: unknown;
    flow_version_etag: string;
    xray_trace_id?: string | null;
    status_code: string;
    parent_flow_id: string | null;
    parent_flow_version_id: string | null;
    parent_decision_id: string | null;
    source_entry_id: string | null;
    linked_decisions: null | Record<
      string,
      Record<
        string,
        {
          decision_id: string;
          originating_node_id: string;
          is_called_from_loop?: boolean;
          loop_index?: number;
        }
      >
    >;
    durations?: Array<DecisionHistoryIntegrationDuration>;
  };

export type DecisionHistoryRecordV3 = (
  | {
      is_error: false;
    }
  | { is_error: true; response: DecisionHistoryErrorResponse }
) &
  HistoricalDecisionApiResponse;

type OriginFilterOptions = "api_call" | "job" | "flow";

export type DecisionHistoryFiltersV2 = {
  order?: "asc" | "desc";
  timeWindow?: DateRange;
  environment?: DecisionEnvironment;
  fields: Partial<{
    status_code: DecisionStatusCode;
    status: "success" | "pending" | "timeout" | "canceled" | "failure";
    flow_version_id: string;
    entity_id: string | null;
    parent_decision_id: string | null;
    entity_or_parent_decision_id: string | null;
    parent_flow_id: string;
    parent_flow_version_id: string;
    job_id: string;
    job_run_id: string;
    origin: OriginFilterOptions;
  }>;
};

export type GetDecisionsParams = {
  baseUrl: string;
  flowSlug: string;
  filters: DecisionHistoryFiltersV2;
  limit: number;
  include_node_results?: boolean;
  include_request_data?: boolean;
  include_response_data?: boolean;
  include_integration_durations?: boolean;
  include_execution_metadata?: boolean;
  parity_check?: boolean;
};

export type DecisionHistoryPage<
  ResponseData extends GenericObject = GenericObject,
> = {
  data: DecisionHistoryRecordV2<ResponseData>[];
  page_min_timestamp: string;
  page_max_timestamp: string;
};

type DecisionHistoryQueryParams = Omit<GetDecisionsParams, "limit">;
type DecisionHistoryQueryKeyParams = Omit<
  DecisionHistoryQueryParams,
  "filters"
> & { filters: DecisionHistoryFiltersV2 | null };

export type DevisionHistoryV3GetRequest = Omit<
  DecisionsApiListDecisionsHistoryApiV3DecisionsGetRequest,
  "outcomeFilters"
> & {
  outcomeFilters?: DecisionsOutcomeFilter[];
};

export type DecisionHistoryV3QueryParams = Omit<
  DevisionHistoryV3GetRequest,
  "limit"
> & {
  enabled?: boolean;
  baseUrl: string;
  icebergOverride?: boolean;
  // Make these required to nudge people towards passing partition filters
  environments: string;
  flowIds: string;
  startStartTime: string;
  endStartTime: string;
};

export const buildHistoryDecisionV2QueryKey = ({
  baseUrl,
  flowSlug,
  include_node_results,
  include_integration_durations,
  include_request_data,
  include_response_data,
  include_execution_metadata,
  filters,
  isIcebergEnabled,
}: DecisionHistoryQueryKeyParams & { isIcebergEnabled: boolean }) => {
  const baseKey = [
    "decisions",
    baseUrl,
    flowSlug,
    include_node_results,
    include_integration_durations,
    include_request_data,
    include_response_data,
    include_execution_metadata,
    isIcebergEnabled,
  ];

  return [...baseKey, ...(filters ? [filters] : [])];
};

export const decisionsV3BaseKey = ["decision", "v3"];

export const buildHistoryDecisionsV3QueryKey = (
  params: DecisionHistoryV3QueryParams,
) => {
  return [
    ...decisionsV3BaseKey,
    ...(Object.keys(params).sort() as (keyof DecisionHistoryV3QueryParams)[])
      .filter(
        (k) => k !== "pageToken" && k !== "enabled" && k !== "icebergOverride",
      )
      .map((k) => params[k]),
  ];
};

export const useHistoryDecisionsV2 = <
  ResponseData extends GenericObject = GenericObject,
>(
  params: DecisionHistoryQueryParams & {
    enabled?: boolean;
    icebergOverride?: boolean;
    refetchInterval?: (
      query: Query<
        DecisionHistoryPage<ResponseData>,
        Error,
        InfiniteData<DecisionHistoryPage<ResponseData>, string | undefined>,
        QueryKey
      >,
    ) => number | false | undefined;
  },
) => {
  const isIcebergEnabled = useIsIcebergEnabled();
  const effectiveIceberg = params.icebergOverride ?? isIcebergEnabled;
  const queryKey = buildHistoryDecisionV2QueryKey({
    ...params,
    isIcebergEnabled: effectiveIceberg,
  });
  // Reset the cached page size when the query identity changes. We adjust
  // during render (not via useEffect) — see decide-ui CLAUDE.md. hashKey
  // matches react-query's own identity hashing (deep, key-sorted), so a
  // filter object rebuilt with the same values won't spuriously reset.
  const queryKeyHash = hashKey(queryKey);
  const pageSizeRef = useRef<number>(DECISION_HISTORY_DEFAULT_PAGE_SIZE);
  const lastQueryKeyRef = useRef<string>(queryKeyHash);
  if (lastQueryKeyRef.current !== queryKeyHash) {
    lastQueryKeyRef.current = queryKeyHash;
    pageSizeRef.current = DECISION_HISTORY_DEFAULT_PAGE_SIZE;
  }

  const env: "sandbox" | "prod" | "all" = (() => {
    if (params.filters.environment === undefined) {
      return "all";
    }

    return params.filters.environment === DecisionEnvironment.LIVE
      ? "prod"
      : "sandbox";
  })();

  return useInfiniteQuery<
    DecisionHistoryPage<ResponseData>,
    Error,
    InfiniteData<DecisionHistoryPage<ResponseData>>,
    QueryKey,
    string | undefined
  >({
    queryKey,
    queryFn: async ({
      pageParam,
    }): Promise<DecisionHistoryPage<ResponseData>> => {
      const paginationFilters = (() => {
        const order = params.filters.order;
        if (order === "asc") {
          return {
            before_timestamp: params.filters.timeWindow?.to?.toISOString(),
            after_timestamp:
              pageParam ?? params.filters.timeWindow?.from?.toISOString(),
          };
        }

        return {
          before_timestamp:
            pageParam ?? params.filters.timeWindow?.to?.toISOString(),
          after_timestamp: params.filters.timeWindow?.from?.toISOString(),
        };
      })();
      const queryKeyHashAtFetch = lastQueryKeyRef.current;
      const requestedPageSize = pageSizeRef.current;
      const { data: response, pageSize: effectivePageSize } =
        await fetchDecisionsWithPageSizeFallback(
          (pageSize) =>
            decisionHistoryApiRoute.get<{
              decisions: DecisionHistoryRecordV2<ResponseData>[];
              page_max_timestamp: string;
              page_min_timestamp: string;
            }>("/decisions", {
              baseURL: params.baseUrl,
              params: {
                flow_slug: params.flowSlug,
                ...paginationFilters,
                environment: env,
                limit: pageSize,
                only_finalized_decisions: false,
                include_extra_fields: true,
                include_node_results: params.include_node_results,
                include_request_data: params.include_request_data,
                include_response_data: params.include_response_data,
                include_execution_metadata: params.include_execution_metadata,
                iceberg: effectiveIceberg,
                parity_check: params.parity_check,
                order: params.filters.order,
                ...params.filters.fields,
              },
            }),
          requestedPageSize,
        );
      // Only write back if the query identity hasn't changed during the fetch
      // — otherwise a stale in-flight request could poison the next query's ref.
      if (lastQueryKeyRef.current === queryKeyHashAtFetch) {
        pageSizeRef.current = effectivePageSize;
      }
      if (effectivePageSize < requestedPageSize) {
        logger.warn("decision_history.page_size_halved", {
          endpoint: "v2",
          from: requestedPageSize,
          to: effectivePageSize,
        });
      }
      return {
        data: response.data.decisions,
        page_min_timestamp: response.data.page_min_timestamp,
        page_max_timestamp: response.data.page_max_timestamp,
      };
    },
    getNextPageParam: (lastPage) => {
      return lastPage.data.length > 0
        ? params.filters.order === "asc"
          ? lastPage.page_max_timestamp
          : lastPage.page_min_timestamp
        : undefined;
    },
    initialPageParam: undefined,
    staleTime: 0,
    refetchInterval: params.refetchInterval,
    // If we don't refetch on mount, changing the filters may show out of date data
    refetchOnMount: true,
    enabled: params.enabled,
  });
};

export const useHistoryDecisionsV3 = (params: DecisionHistoryV3QueryParams) => {
  const isIcebergEnabled = useIsIcebergEnabledSafe();
  const effectiveIceberg = params.icebergOverride ?? isIcebergEnabled;

  if (params.outcomeFilters?.length === 0) {
    params.outcomeFilters = undefined;
  }

  const queryKey = buildHistoryDecisionsV3QueryKey({
    ...params,
    iceberg: effectiveIceberg,
  });
  // Reset the cached page size when the query identity changes (see v2 above).
  // `params.pageSize` is part of the queryKey, so a caller-driven change to it
  // re-initialises the ref to the new value via this same path.
  const queryKeyHash = hashKey(queryKey);
  const initialPageSize = params.pageSize ?? DECISION_HISTORY_DEFAULT_PAGE_SIZE;
  const pageSizeRef = useRef<number>(initialPageSize);
  const lastQueryKeyRef = useRef<string>(queryKeyHash);
  if (lastQueryKeyRef.current !== queryKeyHash) {
    lastQueryKeyRef.current = queryKeyHash;
    pageSizeRef.current = initialPageSize;
  }

  return useInfiniteQuery<
    DecisionList,
    Error,
    InfiniteData<DecisionList>,
    QueryKey,
    string | undefined
  >({
    queryKey,
    queryFn: async ({ pageParam }): Promise<DecisionList> => {
      const queryKeyHashAtFetch = lastQueryKeyRef.current;
      const requestedPageSize = pageSizeRef.current;
      const { data, pageSize: effectivePageSize } =
        await fetchDecisionsWithPageSizeFallback(async (pageSize) => {
          const response = await historyApi(
            params.baseUrl,
          ).listDecisionsHistoryApiV3DecisionsGet({
            ...params,
            pageSize,
            ...(pageParam && { pageToken: pageParam }),
            ...(Array.isArray(params.outcomeFilters)
              ? {
                  outcomeFilters: btoa(JSON.stringify(params.outcomeFilters)),
                }
              : { outcomeFilters: undefined }),
            iceberg: effectiveIceberg,
          });
          return response.data;
        }, requestedPageSize);
      // Skip the ref write if the query identity changed during the fetch
      // — see v2 above.
      if (lastQueryKeyRef.current === queryKeyHashAtFetch) {
        pageSizeRef.current = effectivePageSize;
      }
      if (effectivePageSize < requestedPageSize) {
        logger.warn("decision_history.page_size_halved", {
          endpoint: "v3",
          from: requestedPageSize,
          to: effectivePageSize,
        });
      }
      return data;
    },
    getNextPageParam: (lastPage) => {
      return lastPage.decisions.length > 0 && lastPage.next_page_token !== null
        ? lastPage.next_page_token
        : undefined;
    },
    initialPageParam: undefined,
    staleTime: 0,
    // If we don't refetch on mount, changing the filters may show out of date data
    refetchOnMount: true,
    retry: (failureCount, error) => {
      if (isAxiosError(error)) {
        const status = error.response?.status;
        if (status === 403 || status === 408 || status === 429) {
          return false;
        }
      }
      return failureCount < 3;
    },
    enabled: params.enabled,
  });
};

export type GetDecisionParams = {
  baseUrl: string;
  decisionId: string;
  includeExtraFields?: boolean;
  signal?: AbortSignal;
  includeNodeResultsData?: boolean;
  includeLinkedDecisions?: boolean;
  includeIntegrationDurations?: boolean;
  parityCheck?: boolean;
  iceberg?: boolean;
  flowId?: string;
  environment?: "live" | "sandbox";
};

export const getHistoryDecision = async <
  ResponseData extends GenericObject = GenericObject,
>(
  params: GetDecisionParams,
) =>
  await decisionHistoryApiRoute.get<DecisionHistoryRecordV2<ResponseData>>(
    `/decisions/${params.decisionId}`,
    {
      baseURL: params.baseUrl,
      params: {
        include_extra_fields: params.includeExtraFields || undefined,
        include_linked_decisions: params.includeLinkedDecisions !== false,
        include_node_results_data: params.includeNodeResultsData !== false,
        include_integration_durations:
          params.includeIntegrationDurations !== false,
        parity_check: params.parityCheck,
        iceberg: params.iceberg,
        flow_id: params.flowId,
        environment: params.environment,
      },
      signal: params.signal,
    },
  );
const HISTORY_DECISION_STALE_TIME_MS = 5 * 60 * 1000;

export const historyDecisionKeys = {
  detail: (baseUrl: string, decisionId: string, iceberg: boolean) =>
    ["decisionsV2", "detail", baseUrl, decisionId, iceberg] as const,
};

/**
 * One decision by id, regardless of the flow, version or environment it ran
 * in — the copilot's decision resolver and deeplink share this entry through
 * `ensureQueryData`, so a warm cache answers without a request. A decision is
 * immutable once written, hence the long stale time.
 */
export const historyDecisionQueryOptions = ({
  baseUrl,
  decisionId,
  iceberg,
}: {
  baseUrl: string;
  decisionId: string;
  iceberg: boolean;
}) => ({
  queryKey: historyDecisionKeys.detail(baseUrl, decisionId, iceberg),
  queryFn: async (): Promise<DecisionHistoryRecordV2> =>
    (
      await getHistoryDecision({
        baseUrl,
        decisionId,
        iceberg,
        includeNodeResultsData: false,
        includeLinkedDecisions: false,
        includeIntegrationDurations: false,
      })
    ).data,
  staleTime: HISTORY_DECISION_STALE_TIME_MS,
  retry: false,
});

export const NO_DECISION_FOUND = "noneFound" as const;
export const FLOW_MISMATCH = "flowMismatch" as const;
export const VERSION_MISMATCH = "versionMismatch" as const;
export const ENVIRONMENT_MISMATCH_IS_PROD = "isProdDecision" as const;
export const ENVIRONMENT_MISMATCH_IS_SANDBOX = "isSandboxDecision" as const;
export const DECISION_FORBIDDEN = "forbidden" as const;
export type SingleDecisionQueryErrorReason =
  | typeof NO_DECISION_FOUND
  | typeof DECISION_FORBIDDEN
  | typeof FLOW_MISMATCH
  | typeof VERSION_MISMATCH
  | typeof ENVIRONMENT_MISMATCH_IS_PROD
  | typeof ENVIRONMENT_MISMATCH_IS_SANDBOX;

export type SingleDecisionEnvelope<
  T extends DecisionHistoryRecordV2 | HistoricalDecisionApiResponse,
> =
  | {
      isQueryError: true;
      reason: SingleDecisionQueryErrorReason;
    }
  | {
      isQueryError: false;
      decision: T;
    };
type DecisionMatchParams = {
  versionIdToMatch?: string;
  flowIdToMatch?: string;
  environmentToMatch?: DecisionEnvironment;
  initialData?: SingleDecisionEnvelope<DecisionHistoryRecordV2>;
};

export const useHistoryDecisionV2 = <
  ResponseData extends GenericObject = GenericObject,
>(
  params: GetDecisionParams & DecisionMatchParams,
) => {
  // Using the safe variant here because this hook is once used from outside workspace context with an overwrite.
  const isIcebergEnabledFromHook = useIsIcebergEnabledSafe();
  const isIcebergEnabled = params.iceberg ?? isIcebergEnabledFromHook;

  const noDecisionFound = {
    isQueryError: true,
    reason: NO_DECISION_FOUND,
  } as const;
  const decisionForbidden = {
    isQueryError: true,
    reason: DECISION_FORBIDDEN,
  } as const;
  return useQuery<SingleDecisionEnvelope<DecisionHistoryRecordV2>, Error>({
    queryKey: ["decisionsV2", omit(params, "initialData"), isIcebergEnabled],
    queryFn: async () => {
      try {
        if (!isValidUUID(params.decisionId)) {
          return noDecisionFound;
        }
        const decision = (
          await getHistoryDecision<ResponseData>({
            ...params,
            iceberg: isIcebergEnabled,
          })
        ).data;
        const flowMatches = params.flowIdToMatch
          ? decision.flow.id === params.flowIdToMatch
          : true;
        if (!flowMatches) {
          return { isQueryError: true, reason: FLOW_MISMATCH };
        }
        const versionMatches = params.versionIdToMatch
          ? decision.flow.version_id === params.versionIdToMatch
          : true;
        if (!versionMatches)
          return { isQueryError: true, reason: VERSION_MISMATCH };
        if (
          params.environmentToMatch === DecisionEnvironment.SANDBOX &&
          !decision.is_sandbox
        )
          return { isQueryError: true, reason: ENVIRONMENT_MISMATCH_IS_PROD };

        if (
          params.environmentToMatch === DecisionEnvironment.LIVE &&
          decision.is_sandbox
        )
          return {
            isQueryError: true,
            reason: ENVIRONMENT_MISMATCH_IS_SANDBOX,
          };

        return { isQueryError: false, decision };
      } catch (error: unknown) {
        const status = (error as AxiosError).response?.status;
        if (status === 403) {
          return decisionForbidden;
        }
        if (status === 422 || status === 404) {
          return noDecisionFound;
        }
        throw error;
      }
    },
    enabled: Boolean(params.decisionId) && params.baseUrl !== "",
    retry: 1,
    staleTime: 0,
    gcTime: 0,
    initialData: params.initialData,
  });
};

/**
 * Basic query for a decision.
 * Without handling any special cases, just fetches decision data.
 */
export const useHistoryDecisionV3Basic = (
  params: DecisionsApiGetDecisionHistoryApiV3DecisionsDecisionIdGetRequest & {
    baseUrl: string;
  },
  options?: Pick<
    UseQueryOptions<HistoricalDecisionApiResponse, AxiosError>,
    "enabled" | "retry" | "staleTime" | "gcTime"
  >,
) => {
  const isIcebergEnabled = useIsIcebergEnabled();

  return useQuery<HistoricalDecisionApiResponse, AxiosError>({
    queryKey: ["decisions", "v3", "base", params, isIcebergEnabled],
    queryFn: async () => {
      return (
        await historyApi(
          params.baseUrl,
        ).getDecisionHistoryApiV3DecisionsDecisionIdGet({
          ...params,
          iceberg: isIcebergEnabled,
        })
      ).data;
    },
    enabled: Boolean(params.baseUrl) && isValidUUID(params.decisionId),
    // A 403 is an authorization verdict on the decision's environment, not a
    // transient failure, so retrying it can never succeed.
    retry: (failureCount, error) =>
      error.response?.status === 403 ? false : failureCount < 1,
    staleTime: 0,
    gcTime: 0,
    ...options,
  });
};

export const useHistoryDecisionV3 = (
  params: DecisionsApiGetDecisionHistoryApiV3DecisionsDecisionIdGetRequest & {
    baseUrl: string;
    versionIdToMatch?: string;
    flowIdToMatch?: string;
    environmentToMatch?: DecisionEnvironment;
    initialData?: SingleDecisionEnvelope<HistoricalDecisionApiResponse>;
  },
) => {
  const isIcebergEnabled = useIsIcebergEnabled();

  const noDecisionFound = {
    isQueryError: true,
    reason: NO_DECISION_FOUND,
  } as const;
  const decisionForbidden = {
    isQueryError: true,
    reason: DECISION_FORBIDDEN,
  } as const;
  return useQuery<SingleDecisionEnvelope<HistoricalDecisionApiResponse>, Error>(
    {
      queryKey: [
        "decisions",
        "v3",
        omit(params, "initialData"),
        isIcebergEnabled,
      ],
      queryFn: async () => {
        try {
          if (!isValidUUID(params.decisionId)) {
            return noDecisionFound;
          }
          const decision = (
            await historyApi(
              params.baseUrl,
            ).getDecisionHistoryApiV3DecisionsDecisionIdGet({
              ...params,
              iceberg: isIcebergEnabled,
            })
          ).data;
          const flowMatches = params.flowIdToMatch
            ? decision.flow?.id === params.flowIdToMatch
            : true;
          if (!flowMatches) {
            return { isQueryError: true, reason: FLOW_MISMATCH };
          }
          const versionMatches = params.versionIdToMatch
            ? decision?.flow?.version?.id === params.versionIdToMatch
            : true;
          if (!versionMatches)
            return { isQueryError: true, reason: VERSION_MISMATCH };
          if (
            params.environmentToMatch !== undefined &&
            decision.environment === DecisionEnvironment.LIVE &&
            params.environmentToMatch !== DecisionEnvironment.LIVE
          ) {
            return {
              isQueryError: true,
              reason: ENVIRONMENT_MISMATCH_IS_PROD,
            };
          }
          if (
            params.environmentToMatch !== undefined &&
            decision.environment === DecisionEnvironment.SANDBOX &&
            params.environmentToMatch !== DecisionEnvironment.SANDBOX
          ) {
            return {
              isQueryError: true,
              reason: ENVIRONMENT_MISMATCH_IS_SANDBOX,
            };
          }

          return { isQueryError: false, decision };
        } catch (error: unknown) {
          const status = isAxiosError(error)
            ? error.response?.status
            : undefined;
          if (status === 403) {
            return decisionForbidden;
          }
          if (status === 422 || status === 404) {
            return noDecisionFound;
          }
          throw error;
        }
      },
      enabled: Boolean(params.decisionId) && Boolean(params.baseUrl),
      retry: 1,
      staleTime: 0,
      gcTime: 0,
      initialData: params.initialData,
    },
  );
};

export const useNodeResultData = (params: {
  nodeId: string;
  decisionId: string;
  baseUrl: string;
}) => {
  const isIcebergEnabled = useIsIcebergEnabled();

  return useQuery<GenericObject>({
    queryKey: ["nodeResultData", params, isIcebergEnabled],
    queryFn: async () => {
      const response = await decisionHistoryV3ApiRoute.get(
        `decisions/${params.decisionId}/node_results/${params.nodeId}`,
        {
          params: { $fields: "data", iceberg: isIcebergEnabled },
          baseURL: params.baseUrl,
        },
      );
      return response.data.data;
    },
    enabled: !!params.decisionId && !!params.nodeId,
  });
};
